import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Download, Eye, EyeOff, FileJson, Loader2, RefreshCw, AlertTriangle, Map } from "lucide-react";
import { appsApi, errMsg, notifyError, fmtDate, fmtSize } from "@/lib/appsApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/apps`;

const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success("Copied")).catch(() => notifyError("Copy failed: your browser blocked clipboard access. Select the text and copy it manually."));

const UrlRow = ({ label, url, testid }) => (
  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3" data-testid={testid}>
    <span className="w-28 shrink-0 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">{label}</span>
    <code className="min-w-0 flex-1 break-all rounded-xl bg-[#F4F9FD] px-3 py-2 text-xs text-ink">{url}</code>
    <button onClick={() => copy(url)} className="self-start rounded-full p-2 text-slate-500 hover:bg-sky-50 hover:text-sky-700 sm:self-auto" aria-label={`Copy ${label}`} data-testid={`${testid}-copy`}><Copy className="h-4 w-4" /></button>
  </div>
);

const SharedCard = ({ s, onReplaced }) => {
  const [busy, setBusy] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const origin = window.location.origin;

  const replace = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await appsApi.post(`/admin/shared/${s.name}`, fd);
      toast.success(r?.data?.valid_json === false ? "Replaced — note: file is not valid JSON" : "Replaced — live on the keyed link now");
      onReplaced();
    } catch (err) {
      notifyError(errMsg(err, `Replacing ${s.name}`));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <article className="rounded-3xl border border-sky-100 bg-white p-5 sm:p-6" data-testid={`shared-file-${s.name}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-50 text-sky-700"><FileJson className="h-5 w-5" /></span>
          <div>
            <p className="font-display text-lg font-semibold text-ink">{s.name}</p>
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500" data-testid={`shared-file-meta-${s.name}`}>
              {fmtSize(s.size)} · {s.updated_at ? `replaced ${fmtDate(s.updated_at)}` : "original upload"}
            </p>
            {!s.valid_json && <p className="mt-1 inline-flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="h-3.5 w-3.5" />Current file is not valid JSON</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate("/apps/pls")} className="inline-flex items-center gap-2 rounded-full bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700" data-testid={`shared-file-explore-${s.name}`}><Map className="h-4 w-4" />Explore</button>
          <a href={`${API}/admin/shared/${s.name}/download`} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm text-ink hover:border-sky-600" data-testid={`shared-file-download-${s.name}`}><Download className="h-4 w-4" />Current</a>
          <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 ${busy ? "pointer-events-none opacity-60" : ""}`} data-testid={`shared-file-replace-${s.name}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Replace
            <input ref={ref} type="file" accept=".json,application/json" className="sr-only" onChange={(e) => replace(e.target.files?.[0])} data-testid={`shared-file-input-${s.name}`} />
          </label>
        </div>
      </div>
      <div className="mt-5 space-y-3">
        <UrlRow label="Keyed link" url={`${origin}${s.keyed_path}`} testid={`shared-keyed-url-${s.name}`} />
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3" data-testid={`shared-api-key-${s.name}`}>
          <span className="w-28 shrink-0 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">API key</span>
          <code className="min-w-0 flex-1 break-all rounded-xl bg-[#F4F9FD] px-3 py-2 text-xs text-ink">{showKey ? s.api_key : "•".repeat(24)}</code>
          <div className="flex">
            <button onClick={() => setShowKey(!showKey)} className="rounded-full p-2 text-slate-500 hover:bg-sky-50 hover:text-sky-700" aria-label="Show key" data-testid={`shared-api-key-toggle-${s.name}`}>{showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
            <button onClick={() => copy(s.api_key)} className="rounded-full p-2 text-slate-500 hover:bg-sky-50 hover:text-sky-700" aria-label="Copy key" data-testid={`shared-api-key-copy-${s.name}`}><Copy className="h-4 w-4" /></button>
          </div>
        </div>
        <UrlRow label="Public link" url={`${origin}${s.public_path}`} testid={`shared-public-url-${s.name}`} />
        <p className="text-xs leading-relaxed text-slate-500">Replacing updates the <strong>keyed link</strong> straight away. The public link is part of the website itself and only changes when the site is redeployed.</p>
      </div>
    </article>
  );
};

export const SharedFiles = () => {
  const [items, setItems] = useState([]);
  const load = () => appsApi.get("/admin/shared").then((r) => setItems(Array.isArray(r?.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => {
    load();
  }, []);

  return (
    <section className="mt-10" data-testid="shared-files-section">
      <h3 className="font-display text-xl font-semibold text-ink">Shared data files</h3>
      <p className="mt-1 text-sm text-slate-600">Files other apps read from this site. Only admins see this.</p>
      <div className="mt-4 space-y-4">{items.map((s) => <SharedCard key={s.name} s={s} onReplaced={load} />)}</div>
    </section>
  );
};
